import type {
	ActiveProductTypeEnum,
	AllowedPaymentMethodsEnum,
	ApplicationStateEnum,
	ApplicationStateReasonEnum,
	AvailableAmountStateEnum,
	AvailablePayInThreeAmountStateEnum,
	BillingDefermentUnavailableStateReasonEnum,
	CardTypeEnum,
	InstallmentContractStateEnum,
	InstallmentsLoanTypeEnum,
	LimitAmendmentEntryStateEnum,
	MarketingCampaignEnum,
	OrderStateEnum,
	OutageAffectedSystemEnum,
	PaymentAvailabilityMethodsEnum,
	PaymentAvailabilityReasonEnum,
	PaymentCardStateEnum,
	PaymentCardStateReasonEnum,
	RevolvingLoanApplicationStateEnum,
	RevolvingLoanStateEnum,
	RevolvingTransactionTypeEnum,
	TransferBonusTypeEnum,
	WalletEnum,
} from '.spec/self-care/tsTypes';

import type { Amount } from '@skippay/core/@types/amount';

import type { ActionFlag, BlockedCategory } from './masking';

type Money = number; // toFloat(Amount)
export type IsoDate = string; // YYYY-MM-DD

export enum JarabotSurface {
	SELFCARE = 'selfcare',
	GATE = 'gate',
}

export interface JarabotIdentity {
	userId: string;
	name?: string;
	email?: string;
	phone?: string;
	countryCode?: string;
	kycIsOutdated?: boolean;
	amlLegalReviewRequested?: boolean;
}

export interface PayableRef {
	id: string;
	value: Amount;
}

export interface Eligibility {
	canApplyForRevolving?: boolean;
	canApplyForInstallments?: boolean;
	availableLoanTypes?: InstallmentsLoanTypeEnum[];
}

export enum AccountPackage {
	MINI = 'mini',
	MAXI = 'maxi',
}

export interface AccountInfo {
	package?: AccountPackage;
	hasPreferredBank?: boolean;
}

export enum KycState {
	VERIFIED = 'verified',
	OUTDATED = 'outdated',
	UNKNOWN = 'unknown',
}

export interface Compliance {
	actionsRequired?: ActionFlag[];
	blockedCategories?: BlockedCategory[];
	kycState?: KycState;
	documentExpiry?: IsoDate;
	isDocumentExpired?: boolean;
	amlReviewExpiresAt?: IsoDate;
}

export interface PayInThreeLimit {
	available?: Money;
	total?: Money;
	minimalPurchase?: Money;
	state?: AvailablePayInThreeAmountStateEnum;
}

export interface LimitInfo {
	available?: Money;
	total?: Money;
	state?: AvailableAmountStateEnum;
	annualAvailable?: Money;
	annualTotal?: Money;
	amendmentState?: LimitAmendmentEntryStateEnum;
	amendmentMaxAllowed?: Money;
	increaseOfferAvailable?: boolean;
	payInThree?: PayInThreeLimit;
}

export interface RevolvingInfo {
	state?: RevolvingLoanStateEnum;
	applicationState?: RevolvingLoanApplicationStateEnum;
	activatedDate?: IsoDate;
}

export interface BillingDeferment {
	fee?: Money;
	feeDiscount?: Money;
	amountToDefer?: Money;
	totalValue?: Money;
	count?: number;
}

export interface BillingCashback {
	available?: Money;
	applied?: Money;
	isApplicable?: boolean;
	isApplied?: boolean;
}

export interface BillingInstallments {
	canApply?: boolean;
	maxPrincipal?: Money;
}

export interface BillingTransferBonus {
	type?: TransferBonusTypeEnum;
	amount?: Money;
}

export interface BillingInfo {
	payable?: PayableRef;
	balance?: Money;
	total?: Money;
	minimumToPay?: Money;
	dueDate?: IsoDate;
	daysOverdue?: number;
	isPaid?: boolean;
	canPay?: boolean;
	paymentMethods?: PaymentAvailabilityMethodsEnum[];
	canDefer?: boolean;
	period?: string;
	paymentDate?: IsoDate;
	whyCannotPay?: PaymentAvailabilityReasonEnum[];
	deferment?: BillingDeferment;
	defermentUnavailableReason?: BillingDefermentUnavailableStateReasonEnum;
	cashback?: BillingCashback;
	splitToInstallments?: BillingInstallments;
	transferBonus?: BillingTransferBonus;
	coinReward?: number;
	campaigns?: MarketingCampaignEnum[];
	unpaidCount?: number;
}

export interface NextPayment {
	date?: IsoDate;
	amount?: Money;
}

export interface Sanctions {
	reminders?: Money;
	contractualPenalty?: Money;
	legalInterest?: Money;
}

export interface PayInThreeScheduleItem {
	number?: number;
	dueDate?: IsoDate;
	amount?: Money;
	paymentDate?: IsoDate;
	daysOverdue?: number;
}

export interface PayInThreeItem {
	eshop?: string;
	state?: ApplicationStateEnum;
	stateReason?: ApplicationStateReasonEnum;
	total?: Money;
	debt?: Money;
	next?: NextPayment;
	daysOverdue?: number;
	canPay?: boolean;
	items?: string[];
	schedule?: PayInThreeScheduleItem[];
	sanctionsTotal?: Money;
	sanctions?: Sanctions;
	orderState?: OrderStateEnum;
	hasReturnedItems?: boolean;
	hasOverpayment?: boolean;
	canSplitToInstallments?: boolean;
	orderCreatedDate?: IsoDate;
}

export interface DeferredPaymentItem {
	payable?: PayableRef;
	eshop?: string;
	state?: ApplicationStateEnum;
	stateReason?: ApplicationStateReasonEnum;
	total?: Money;
	debt?: Money;
	dueDate?: IsoDate;
	amountToPay?: Money;
	daysOverdue?: number;
	canPay?: boolean;
	items?: string[];
	sanctions?: Sanctions;
	orderState?: OrderStateEnum;
	hasReturnedItems?: boolean;
	orderCreatedDate?: IsoDate;
}

export interface ClosedApplicationItem {
	eshop?: string;
	state?: ApplicationStateEnum;
	total?: Money;
	paidDate?: IsoDate;
	orderState?: OrderStateEnum;
	hasReturnedItems?: boolean;
}

export interface InstallmentItem {
	type?: InstallmentsLoanTypeEnum;
	state?: InstallmentContractStateEnum;
	next?: NextPayment;
	daysOverdue?: number;
	earlyRepayment?: Money;
	remainingDebt?: Money;
	installmentsCount?: number;
	paidCount?: number;
	interestRate?: string;
	rpsn?: string;
	title?: string;
	repaymentDate?: IsoDate;
	accelerationDate?: IsoDate;
	allowedPaymentMethods?: AllowedPaymentMethodsEnum[];
	sanctionsTotal?: Money;
	sanctions?: Sanctions;
	variableSymbol?: string;
}

export enum ApplicationInProgressType {
	INSTALLMENTS = 'installments',
}

export interface ApplicationInProgressItem {
	type: ApplicationInProgressType;
	state?: InstallmentContractStateEnum;
}

export interface CardInfo {
	state?: PaymentCardStateEnum;
	stateReason?: PaymentCardStateReasonEnum;
	cardType?: CardTypeEnum;
	wallets?: WalletEnum[];
	canShowPin?: boolean;
	onlinePaymentsEnabled?: boolean;
	atmWithdrawalEnabled?: boolean;
	canApplyForNewCardIssuance?: boolean;
	validThru?: string;
	isExpiringSoon?: boolean;
}

export interface CoinsInfo {
	balance?: Money;
	nearestExpiration?: { date?: IsoDate; amount?: Money };
}

export interface CashbackInfo {
	available?: Money;
	pending?: Money;
	totalApplied?: Money;
	totalBonus?: Money;
	nearestExpiration?: { date?: IsoDate; amount?: Money };
}

export interface TransactionItem {
	amount?: Money;
	date?: IsoDate;
	type?: RevolvingTransactionTypeEnum;
	billing?: string;
	merchant?: string;
	isAuthorization?: boolean;
}

export interface OfferItem {
	id: string;
	label?: string;
	description?: string;
	url?: string;
}

export enum PaymentDebtType {
	BILLING = 'BILLING',
	DEFERRED_PAYMENT = 'DEFERRED_PAYMENT',
}

export interface PaymentInstructions {
	account?: string;
	recipientName?: string;
	variableSymbol?: string;
	referenceNumber?: string;
	amount?: Money;
	debtType?: PaymentDebtType;
}

export interface OutageItem {
	system?: OutageAffectedSystemEnum;
	systems?: OutageAffectedSystemEnum[];
	startDate?: IsoDate;
	endDate?: IsoDate | null;
}

export interface InstallmentPlanItem {
	number?: number;
	dueDate?: IsoDate;
	amount?: Money;
}

export interface CurrentApplicationInfo {
	eshop?: string;
	totalPrice?: Money;
	items?: string[];
	financingType?: 'deferredPayment' | 'payInThree';
	flowState?: string;
	rejectionCategory?: string;
	installmentPlan?: InstallmentPlanItem[];
	dueDate?: IsoDate;
	canPayByCard?: boolean;
	canApplyForRevolvingWhenRejected?: boolean;
	requiredPriceLimit?: Money;
	unpaidOrdersSum?: Money;
	isAuthenticated?: boolean;
}

export type CustomerContextDomain =
	| 'eligibility'
	| 'account'
	| 'compliance'
	| 'limit'
	| 'revolving'
	| 'billing'
	| 'unpaidBilling'
	| 'payInThree'
	| 'closedPayInThree'
	| 'deferredPayments'
	| 'closedDeferredPayments'
	| 'installments'
	| 'applicationsInProgress'
	| 'card'
	| 'coins'
	| 'cashback'
	| 'transactions'
	| 'offers'
	| 'outages'
	| 'paymentInstructions'
	| 'currentApplication';

export interface CustomerContext {
	eligibility?: Eligibility;
	account?: AccountInfo;
	activeProducts?: ActiveProductTypeEnum[];
	compliance?: Compliance;
	limit?: LimitInfo;
	revolving?: RevolvingInfo;
	billing?: BillingInfo;
	unpaidBilling?: BillingInfo;
	payInThree?: PayInThreeItem[];
	closedPayInThree?: ClosedApplicationItem[];
	deferredPayments?: DeferredPaymentItem[];
	closedDeferredPayments?: ClosedApplicationItem[];
	installments?: InstallmentItem[];
	applicationsInProgress?: ApplicationInProgressItem[];
	card?: CardInfo;
	cards?: CardInfo[];
	coins?: CoinsInfo;
	cashback?: CashbackInfo;
	transactions?: TransactionItem[];
	offers?: OfferItem[];
	outages?: OutageItem[];
	paymentInstructions?: PaymentInstructions;
	currentApplication?: CurrentApplicationInfo;
}
